/**
 * Single place for job board filter URL serialization.
 * Multi-value filters use repeated query params (e.g. states=CA&states=NY).
 */

export type JobTypeCountsMap = Record<string, number>;

export interface JobBoardFilterData {
    job_type: string;
    page: number;
    staff_types?: string[];
    specialities?: string[];
    states?: string[];
    search?: string;
    start_date?: string;
    end_date?: string;
    min_salary?: number;
    max_salary?: number;
    is_hot_job?: boolean;
}

const DEFAULT_JOB_TYPE = 'all';
const DEFAULT_PAGE = 1;

/** Parse URL search params into filter data (and shape used by filter UI). */
export function searchParamsToFilterData(searchParams: URLSearchParams): JobBoardFilterData {
    const get = (key: string) => searchParams.get(key);
    const getAll = (key: string) => searchParams.getAll(key).filter(Boolean);
    const getNum = (key: string) => {
        const v = searchParams.get(key);
        if (v === null || v === '') return undefined;
        const n = Number(v);
        return Number.isFinite(n) ? n : undefined;
    };

    const staff_types = getAll('staff_types');
    const specialities = getAll('specialities');
    const states = getAll('states');
    const search = get('q') ?? '';
    const start_date = get('start_date') ?? undefined;
    const end_date = get('end_date') ?? undefined;
    const min_salary = getNum('min_salary');
    const max_salary = getNum('max_salary');
    const hot = get('hot');
    const is_hot_job = hot === '1' || hot === 'true';

    return {
        job_type: get('type') || DEFAULT_JOB_TYPE,
        page: Math.max(1, getNum('page') ?? DEFAULT_PAGE),
        ...(staff_types.length ? { staff_types } : {}),
        ...(specialities.length ? { specialities } : {}),
        ...(states.length ? { states } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(start_date ? { start_date } : {}),
        ...(end_date ? { end_date } : {}),
        ...(min_salary != null && min_salary > 0 ? { min_salary } : {}),
        ...(max_salary != null && max_salary < 5000 ? { max_salary } : {}),
        ...(is_hot_job ? { is_hot_job: true } : {}),
    };
}

/** Serialize filter data to URL search params (repeated keys for arrays). */
export function filterDataToSearchParams(filterData: JobBoardFilterData): URLSearchParams {
    const params = new URLSearchParams();
    params.set('type', filterData.job_type || DEFAULT_JOB_TYPE);
    if (filterData.page != null && filterData.page > 1) {
        params.set('page', String(filterData.page));
    }
    filterData.staff_types?.forEach((v) => params.append('staff_types', v));
    filterData.specialities?.forEach((v) => params.append('specialities', v));
    filterData.states?.forEach((v) => params.append('states', v));
    if (filterData.search?.trim()) {
        params.set('q', filterData.search.trim());
    }
    if (filterData.start_date) {
        params.set('start_date', filterData.start_date);
    }
    if (filterData.end_date) {
        params.set('end_date', filterData.end_date);
    }
    if (filterData.min_salary != null && filterData.min_salary > 0) {
        params.set('min_salary', String(filterData.min_salary));
    }
    if (filterData.max_salary != null && filterData.max_salary < 5000) {
        params.set('max_salary', String(filterData.max_salary));
    }
    if (filterData.is_hot_job) {
        params.set('hot', '1');
    }
    return params;
}

/** Filters sent to the job type counts API (same as list API, without job_type/page). */
export function buildCountsFilterPayload(filterData: JobBoardFilterData): Omit<JobBoardFilterData, 'job_type' | 'page'> {
    const { job_type: _jobType, page: _page, ...baseFilters } = filterData;
    return baseFilters;
}

/** Build API payload for websiteJobsList — sends job_types as an array instead of job_type string. */
export function buildApiListPayload(filterData: JobBoardFilterData): Record<string, unknown> {
    const { job_type, page, ...rest } = filterData;
    const payload: Record<string, unknown> = { ...rest };

    if (page != null) {
        payload.page = page;
    }

    if (job_type && job_type !== 'all') {
        payload.job_types = [job_type];
    }

    return payload;
}

/** Stable key for counts refetch — changes only when non-tab, non-pagination filters change. */
export function getCountsFilterKey(filterData: JobBoardFilterData): string {
    return JSON.stringify(buildCountsFilterPayload(filterData));
}

/** Map API job type count keys to tab values; omit keys with no count. */
export function mapJobTypeCountsFromApi(
    jobTypeCounts: Record<string, number | undefined> | null | undefined
): JobTypeCountsMap {
    if (!jobTypeCounts) return {};

    const mapped: JobTypeCountsMap = {};
    Object.entries(jobTypeCounts).forEach(([key, count]) => {
        if (count == null) return;
        const tabValue = key === 'total' ? 'all' : key;
        mapped[tabValue] = count;
    });
    return mapped;
}
